import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { EventRole, LiveEvent } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Box,
    Button,
    Flex,
    Group,
    NumberInput,
    Pagination,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import {
    IconAlertCircle,
    IconCheck,
    IconClipboard,
    IconColumns,
    IconCopy,
    IconFilePlus,
    IconLink,
    IconTrash
} from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import LiveEventCreationForm from "./LiveEventCreationForm";
import LiveEventDeletionForm from "./LiveEventDeletionForm";
import LiveEventInstrumentAssignmentForm from "./LiveEventInstrumentAssignmentForm";
import LiveEventManager from "./LiveEventManager";
import LiveEventSongAssignmentForm from "./LiveEventSongAssignmentForm";
import LiveEventUsersBrowser from "./LiveEventUsersBrowser";

interface LiveEventsBrowserProps {
    onClose: () => void;
}

const LiveEventsBrowser: React.FC<LiveEventsBrowserProps> = ({ onClose }) => {
    // Use 80vh for the container height and 50px for each row (as in the reference)
    const height = 80;
    const rowHeight = 50;

    const [liveEvents, setLiveEvents] = useState<LiveEvent[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [liveEventsPerPage, setLiveEventsPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);

    const auth = useContext(AuthContext);
    if (!auth) {
        throw new Error("LiveEventsBrowser must be a child of AuthContext.");
    }
    const token = auth.token;
    if (!token) return null;

    // Calculate liveEvents per page based on the viewport height
    const calculateLiveEventsPerPage = useCallback(() => {
        // Use 80% of the viewport height minus an estimated 100px for headers/margins
        const containerHeight = window.innerHeight * (height / 100) - 100;
        const perPage = Math.floor(containerHeight / rowHeight);
        setLiveEventsPerPage(perPage);
        if (liveEvents && perPage * (desiredPage - 1) + 1 > liveEvents.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, liveEvents]);

    useEffect(() => {
        calculateLiveEventsPerPage();
        window.addEventListener("resize", calculateLiveEventsPerPage);
        return () => window.removeEventListener("resize", calculateLiveEventsPerPage);
    }, [calculateLiveEventsPerPage]);

    const fetchLiveEvents = () => {
        setLoading(true);

        const notificationId = Utils.displayNotification(
            "Loading Live Events",
            "Fetching all live events, please wait...",
            "blue",
            "loading"
        );

        ConductionAPI.getAllLiveEvents(token)
            .then((loadedLiveEventsInfo: LiveEvent[] | null) => {
                if (!loadedLiveEventsInfo || loadedLiveEventsInfo.length === 0) {
                    setLiveEvents(null);
                    Utils.updateNotification(
                        notificationId,
                        "No Live Events Found",
                        undefined,
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                } else {
                    setLiveEvents(
                        loadedLiveEventsInfo.sort(
                            (a, b) =>
                                b.startTime.getTime() - a.startTime.getTime() // Newest first
                        )
                    );
                    Utils.updateNotification(
                        notificationId,
                        "Live Events Loaded",
                        undefined,
                        "green",
                        <IconCheck size={16} />
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Live Events",
                    "An error occurred while retrieving live events.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchLiveEvents();
    }, [token]);

    const paginatedLiveEvents = useMemo(() => {
        const start = (desiredPage - 1) * liveEventsPerPage;
        return liveEvents?.slice(start, start + liveEventsPerPage) ?? [];
    }, [desiredPage, liveEvents, liveEventsPerPage]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            Utils.displayNotification(
                "Copied to Clipboard",
                `"${text}" is ready to paste!`,
                "green",
                <IconClipboard size={16} />
            );
        });
    };

    const handleEventAdminLimitChange = (eventId: string, newLimit: number | null) => {
        if (newLimit != null && newLimit < 0) {
            Utils.displayNotification(
                "Invalid Value",
                undefined,
                "blue",
                <IconAlertCircle />
            );
            return;
        }

        if (newLimit === 0) {
            newLimit = null;
        }

        const notificationId = Utils.displayNotification(
            "Updating live event admin limit...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.setLiveEventAdminLimit(eventId, newLimit, token)
            .then(() => {
                Utils.updateNotification(
                    notificationId,
                    "Updated live event admin limit",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
                fetchLiveEvents();
            })
            .catch((e: any) => {
                console.warn(e);
                Utils.updateNotification(
                    notificationId,
                    "An Error Occured",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const handleParticipantLimitChange = (eventId: string, newLimit: number | null) => {
        if (newLimit != null && newLimit < 0) {
            Utils.displayNotification(
                "Invalid Value",
                undefined,
                "blue",
                <IconAlertCircle />
            );
            return;
        }

        const notificationId = Utils.displayNotification(
            "Updating live event participant limit...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.setLiveEventParticipantLimit(eventId, newLimit, token)
            .then(() => {
                Utils.updateNotification(
                    notificationId,
                    "updated live event participant limit",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
                fetchLiveEvents();
            })
            .catch((e: any) => {
                console.warn(e);
                Utils.updateNotification(
                    notificationId,
                    "An Error Occured",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const handleRename = (liveEventId: string, newName: string) => {
        if (newName.trim().length <= 0) {
            Utils.displayNotification(
                "Invalid Name",
                undefined,
                "blue",
                <IconAlertCircle />
            );
            return;
        }

        const notificationId = Utils.displayNotification(
            "Renaming liveEvent...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.renameLiveEvent(liveEventId, newName, token)
            .then(() => {
                Utils.updateNotification(
                    notificationId,
                    "Renamed liveEvent",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
                fetchLiveEvents();
            })
            .catch((e: any) => {
                console.warn(e);
                Utils.updateNotification(
                    notificationId,
                    "An Error Occured",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const [showLiveEventCreationForm, setShowLiveEventCreationForm] = useState<boolean>(false);
    const [showLiveEventDeletionFormFor, setShowLiveEventDeletionFormFor] = useState<LiveEvent | null>(null);
    const [showLiveEventInstrumentAssignmentFormFor, setShowLiveEventInstrumentAssignmentFormFor] = useState<LiveEvent | null>(null);
    const [showLiveEventSongAssignmentFormFor, setShowLiveEventSongAssignmentFormFor] = useState<LiveEvent | null>(null);
    const [showLiveEventUsersBrowserFor, setShowLiveEventUsersBrowserFor] = useState<{ liveEvent: LiveEvent, defaultRoleFilter: EventRole } | null>(null);
    const [showLiveEventManagerFor, setShowLiveEventManagerFor] = useState<string | null>(null); // liveEvent ID
    const renderModals = () => (
        <>
            {showLiveEventCreationForm && (
                <LiveEventCreationForm
                    onClose={() => setShowLiveEventCreationForm(false)}
                    onCreated={() => fetchLiveEvents()}
                />
            )}

            {showLiveEventDeletionFormFor && (
                <LiveEventDeletionForm
                    liveEvent={showLiveEventDeletionFormFor}
                    onClose={() => setShowLiveEventDeletionFormFor(null)}
                    onDeleted={() => fetchLiveEvents()}
                />
            )}

            {showLiveEventInstrumentAssignmentFormFor && (
                <LiveEventInstrumentAssignmentForm
                    liveEvent={showLiveEventInstrumentAssignmentFormFor}
                    onClose={() => setShowLiveEventInstrumentAssignmentFormFor(null)}
                    onAssigned={() => fetchLiveEvents()}
                />
            )}

            {showLiveEventSongAssignmentFormFor && (
                <LiveEventSongAssignmentForm
                    liveEvent={showLiveEventSongAssignmentFormFor}
                    onClose={() => setShowLiveEventSongAssignmentFormFor(null)}
                    onAssigned={() => fetchLiveEvents()}
                />
            )}

            {showLiveEventUsersBrowserFor && (
                <LiveEventUsersBrowser
                    liveEvent={showLiveEventUsersBrowserFor.liveEvent}
                    onClose={() => setShowLiveEventUsersBrowserFor(null)}
                    defaultRoleFilter={showLiveEventUsersBrowserFor.defaultRoleFilter}
                />
            )}

            {showLiveEventManagerFor && (
                <LiveEventManager
                    liveEventId={showLiveEventManagerFor}
                    onClose={() => setShowLiveEventManagerFor(null)}
                />
            )}
        </>
    );

    return (
        <ConductionModal
            onClose={onClose}
            title={"Manage Live Events"}
            size={"98%"}
        >
            {renderModals()}
            <Stack align="center" justify="center">
                <Button
                    onClick={() => setShowLiveEventCreationForm(true)}
                >
                    <IconFilePlus size={16} style={{ marginRight: 8 }} />
                    Create New Live Event
                </Button>

                <Flex
                    direction="column"
                    align="center"
                    justify="space-between"
                    style={{ height: `${height}vh`, width: "100%" }}
                >
                    {loading ? (
                        <Skeleton height="100%" radius="md" />
                    ) : liveEvents === null ? (
                        <Text>No live events found.</Text>
                    ) : (
                        <ScrollArea style={{ width: "100%" }}>
                            <Table striped stickyHeader highlightOnHover withColumnBorders>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>Created</Table.Th>
                                        <Table.Th>Start Time</Table.Th>
                                        <Table.Th>End Time</Table.Th>
                                        <Table.Th>Event Admins</Table.Th>
                                        <Table.Th>Participants</Table.Th>
                                        <Table.Th>Players</Table.Th>
                                        <Table.Th>Songs</Table.Th>
                                        <Table.Th>Admin Code</Table.Th>
                                        <Table.Th>Participant Code</Table.Th>
                                        <Table.Th>Player Code</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {paginatedLiveEvents.map((liveEvent: LiveEvent) => {
                                        return (
                                            <Table.Tr key={liveEvent.name} style={{ minHeight: rowHeight }}>
                                                <Table.Td>
                                                    <Group>
                                                        <TextInput
                                                            value={liveEvent.desiredName}
                                                            style={{
                                                                width: 80
                                                            }}
                                                            size="compact-xs"
                                                            maxLength={30}
                                                            onChange={(e) => {
                                                                const newLiveEvents = liveEvents.map((c) => {
                                                                    if (c.id != liveEvent.id) return c;
                                                                    return liveEvent.setDesiredName(e.target.value);
                                                                });
                                                                setLiveEvents(newLiveEvents);
                                                            }}
                                                        />
                                                        {liveEvent.desiredName !== liveEvent.name && (
                                                            <Button
                                                                size="compact-xs"
                                                                disabled={liveEvent.name === liveEvent.desiredName}
                                                                onClick={() => handleRename(liveEvent.id, liveEvent.desiredName)}
                                                            >
                                                                <IconCheck size={16} />
                                                            </Button>
                                                        )}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td><Badge size="xs" color="blue">{formatDateString(liveEvent.dateCreated)}</Badge></Table.Td>
                                                <Table.Td>
                                                    <Stack>
                                                        <Badge size="xs" color="blue">{formatDateString(liveEvent.startTime)}</Badge>
                                                        <Badge size="xs" color="blue">{formatTimeString(liveEvent.startTime)}</Badge>
                                                    </Stack>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Stack>
                                                        <Badge size="xs" color="blue">{formatDateString(liveEvent.endTime)}</Badge>
                                                        <Badge size="xs" color="blue">{formatTimeString(liveEvent.endTime)}</Badge>
                                                    </Stack>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"flex-start"}
                                                        align={"center"}
                                                    >
                                                        <Tooltip label={`View event admins from the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="subtle"
                                                                size="compact-xs"
                                                                onClick={() => setShowLiveEventUsersBrowserFor({ liveEvent: liveEvent, defaultRoleFilter: "EventAdmin" })}
                                                            >
                                                                {liveEvent.getEventAdmins()?.length ?? 0}
                                                            </Button>
                                                        </Tooltip>
                                                        <Group>
                                                            <Text size="xs">/</Text>
                                                            <Tooltip label={`Edit live event admin limit for the ${liveEvent.name} live event`} withArrow>
                                                                <Box>
                                                                    <Stack>
                                                                        {liveEvent.desiredEventAdminLimit == 0 && (
                                                                            <Text size="xs">(Unlimited)</Text>
                                                                        )}
                                                                        <Group>
                                                                            <NumberInput
                                                                                style={{
                                                                                    width: 30
                                                                                }}
                                                                                size="compact-xs"
                                                                                value={liveEvent.desiredEventAdminLimit ?? 0}
                                                                                min={0}
                                                                                max={50}
                                                                                onChange={(value) => {
                                                                                    if (typeof value === "string") return;
                                                                                    const newLiveEvents = liveEvents.map((c) => {
                                                                                        if (c.id != liveEvent.id) return c;
                                                                                        return liveEvent.setDesiredEventAdminLimit(value);
                                                                                    });
                                                                                    setLiveEvents(newLiveEvents);
                                                                                }}
                                                                            />
                                                                            {liveEvent.desiredEventAdminLimit !== liveEvent.eventAdminLimit && (
                                                                                <Button
                                                                                    size="compact-xs"
                                                                                    disabled={liveEvent.eventAdminLimit === liveEvent.desiredEventAdminLimit}
                                                                                    onClick={() => handleEventAdminLimitChange(liveEvent.id, liveEvent.desiredEventAdminLimit)}
                                                                                >
                                                                                    <IconCheck size={16} />
                                                                                </Button>
                                                                            )}
                                                                        </Group>
                                                                    </Stack>
                                                                </Box>
                                                            </Tooltip>
                                                        </Group>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"flex-start"}
                                                        align={"center"}
                                                    >
                                                        <Tooltip label={`View participants for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="subtle"
                                                                size="compact-xs"
                                                                onClick={() => setShowLiveEventUsersBrowserFor({ liveEvent: liveEvent, defaultRoleFilter: "Participant" })}
                                                            >
                                                                {liveEvent.getParticipants()?.length ?? 0}
                                                            </Button>
                                                        </Tooltip>
                                                        <Group>
                                                            <Text size="xs">/</Text>
                                                            <Tooltip label={`Edit student limit for the ${liveEvent.name} live event`} withArrow>
                                                                <Box>
                                                                    <Stack>
                                                                        {liveEvent.desiredParticipantLimit == 0 && (
                                                                            <Text size="xs">(Unlimited)</Text>
                                                                        )}
                                                                        <Group>
                                                                            <NumberInput
                                                                                style={{
                                                                                    width: 30
                                                                                }}
                                                                                size="compact-xs"
                                                                                value={liveEvent.desiredParticipantLimit ?? 0}
                                                                                min={0}
                                                                                max={500}
                                                                                onChange={(value) => {
                                                                                    if (typeof value === "string") return;
                                                                                    const newLiveEvents = liveEvents.map((c) => {
                                                                                        if (c.id != liveEvent.id) return c;
                                                                                        return liveEvent.setDesiredStudentLimit(value);
                                                                                    });
                                                                                    setLiveEvents(newLiveEvents);
                                                                                }}
                                                                            />
                                                                            {liveEvent.desiredParticipantLimit !== liveEvent.participantLimit && (
                                                                                <Button
                                                                                    size="compact-xs"
                                                                                    disabled={liveEvent.participantLimit === liveEvent.desiredParticipantLimit}
                                                                                    onClick={() => handleParticipantLimitChange(liveEvent.id, liveEvent.desiredParticipantLimit)}
                                                                                >
                                                                                    <IconCheck size={16} />
                                                                                </Button>
                                                                            )}
                                                                        </Group>
                                                                    </Stack>
                                                                </Box>
                                                            </Tooltip>
                                                        </Group>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"flex-start"}
                                                        align={"center"}
                                                    >
                                                        <Tooltip label={`View instrumentalists for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="subtle"
                                                                size="compact-xs"
                                                                onClick={() => setShowLiveEventUsersBrowserFor({ liveEvent: liveEvent, defaultRoleFilter: "Instrumentalist" })}
                                                            >
                                                                {liveEvent.getInstrumentalists()?.length ?? 0}
                                                            </Button>
                                                        </Tooltip>
                                                        <Group>
                                                            <Text size="xs">/</Text>
                                                            <Tooltip label={`Click to edit instruments for the ${liveEvent.name} live event`} withArrow>
                                                                <Button
                                                                    variant="subtle"
                                                                    size="compact-xs"
                                                                    onClick={() => setShowLiveEventInstrumentAssignmentFormFor(liveEvent)}
                                                                >
                                                                    {liveEvent.getInstruments()?.length ?? 0}
                                                                </Button>
                                                            </Tooltip>
                                                        </Group>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Tooltip label={`Edit song library for the ${liveEvent.name} live event`} withArrow>
                                                        <Button
                                                            variant="subtle"
                                                            size="compact-xs"
                                                            onClick={() => setShowLiveEventSongAssignmentFormFor(liveEvent)}
                                                        >
                                                            {liveEvent.songIds.length}
                                                        </Button>
                                                    </Tooltip>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"space-between"}
                                                    >
                                                        <Tooltip label={`Event admin code for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-xs"
                                                                leftSection={<IconCopy size={14} />}
                                                                onClick={() =>
                                                                    liveEvent.eventAdminAccessCode &&
                                                                    copyToClipboard(liveEvent.eventAdminAccessCode)
                                                                }
                                                            >
                                                                {liveEvent.eventAdminAccessCode}
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip label={`Event admin shortcut for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-xs"
                                                                onClick={() =>
                                                                    liveEvent.eventAdminAccessCode &&
                                                                    copyToClipboard(`${window.location.origin}/register/${liveEvent.eventAdminAccessCode}`)
                                                                }
                                                            >
                                                                <IconLink size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"space-between"}
                                                    >
                                                        <Tooltip label={`Participant code for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-xs"
                                                                leftSection={<IconCopy size={14} />}
                                                                onClick={() =>
                                                                    liveEvent.participantAccessCode &&
                                                                    copyToClipboard(liveEvent.participantAccessCode)
                                                                }
                                                            >
                                                                {liveEvent.participantAccessCode}
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip label={`Participant shortcut for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-xs"
                                                                onClick={() =>
                                                                    liveEvent.participantAccessCode &&
                                                                    copyToClipboard(`${window.location.origin}/register/${liveEvent.participantAccessCode}`)
                                                                }
                                                            >
                                                                <IconLink size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex
                                                        direction={"row"}
                                                        justify={"space-between"}
                                                    >
                                                        <Tooltip label={`Instrumentalist code for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-xs"
                                                                leftSection={<IconCopy size={14} />}
                                                                onClick={() =>
                                                                    liveEvent.instrumentalistAccessCode &&
                                                                    copyToClipboard(liveEvent.instrumentalistAccessCode)
                                                                }
                                                            >
                                                                {liveEvent.instrumentalistAccessCode}
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip label={`Instrumentalist shortcut for the ${liveEvent.name} live event`} withArrow>
                                                            <Button
                                                                variant="light"
                                                                size="compact-xs"
                                                                onClick={() =>
                                                                    liveEvent.instrumentalistAccessCode &&
                                                                    copyToClipboard(`${window.location.origin}/register/${liveEvent.instrumentalistAccessCode}`)
                                                                }
                                                            >
                                                                <IconLink size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Flex>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group>
                                                        <Tooltip
                                                            label={"Open live event admin interface"}
                                                            withArrow
                                                        >
                                                            <Button
                                                                size="compact-xs"
                                                                disabled={loading}
                                                                onClick={() => setShowLiveEventManagerFor(liveEvent.id)}
                                                            >
                                                                <IconColumns size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip
                                                            label={"Delete"}
                                                            withArrow
                                                        >
                                                            <Button
                                                                size="compact-xs"
                                                                disabled={loading}
                                                                onClick={() => setShowLiveEventDeletionFormFor(liveEvent)}
                                                            >
                                                                <IconTrash size={16} />
                                                            </Button>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}

                    {liveEvents && liveEvents.length > liveEventsPerPage && (
                        <Pagination
                            total={Math.ceil(liveEvents.length / liveEventsPerPage)}
                            value={desiredPage}
                            onChange={setDesiredPage}
                        />
                    )}
                </Flex>
            </Stack>
        </ConductionModal>
    );
};

function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate + " UTC");
    return date.toLocaleDateString(); // TODO: fix time
}

function formatTimeString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate + " UTC");
    return date.toLocaleTimeString();
}

export default LiveEventsBrowser;
